import { useEffect, useState } from 'react';
import { useAuthStore } from './auth/authStore';
import { api, ApiError } from './api/client';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { UsersPage } from './pages/UsersPage';
import { SettingsPage } from './pages/SettingsPage';
import { FramesPage } from './pages/FramesPage';
import { CharactersPage } from './pages/CharactersPage';
import { SkillsPage } from './pages/SkillsPage';
import { AudioPage } from './pages/AudioPage';
import { AuditPage } from './pages/AuditPage';
import { IapPage } from './pages/IapPage';
import { HoldSkinsPage } from './pages/HoldSkinsPage';
import { TipsPage } from './pages/TipsPage';
import { PushPage } from './pages/PushPage';
import { FinishersPage } from './pages/FinishersPage';
import { CombosPage } from './pages/CombosPage';
import { BossesPage } from './pages/BossesPage';
import { ClanEmblemsPage } from './pages/ClanEmblemsPage';
import { BackgroundsPage } from './pages/BackgroundsPage';

type Tab = 'dashboard' | 'users' | 'settings' | 'bosses' | 'combos' | 'frames' | 'finishers' | 'backgrounds' | 'characters' | 'skills' | 'holdskins' | 'clanlogos' | 'tips' | 'audio' | 'iap' | 'push' | 'audit';

const NAV: Array<{ id: Tab; label: string; icon: string }> = [
  { id: 'dashboard', label: 'Dashboard', icon: '◧' },
  { id: 'users', label: 'Players', icon: '⚏' },
  { id: 'settings', label: 'Game Config', icon: '✦' },
  { id: 'bosses', label: 'Boss Break', icon: '☄' },
  { id: 'characters', label: 'Characters', icon: '☺' },
  { id: 'combos', label: 'Shop Combos', icon: '✦' },
  { id: 'skills', label: 'Skill Skins', icon: '✸' },
  { id: 'holdskins', label: 'Hold Skins', icon: '⬡' },
  { id: 'clanlogos', label: 'Clan', icon: '🛡' },
  { id: 'tips', label: 'Match Tips', icon: '💡' },
  { id: 'finishers', label: 'Finishers', icon: '💥' },
  { id: 'frames', label: 'Avatar Frames', icon: '◈' },
  { id: 'backgrounds', label: 'Match Backgrounds', icon: '▧' },
  { id: 'audio', label: 'Audio', icon: '♪' },
  { id: 'iap', label: 'IAP Products', icon: '$' },
  { id: 'push', label: 'Push', icon: '✉' },
  { id: 'audit', label: 'Audit Log', icon: '◐' },
];

export default function App() {
  const status = useAuthStore((s) => s.status);
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);
  const setSession = useAuthStore((s) => s.setSession);
  const setStatus = useAuthStore((s) => s.setStatus);
  const clear = useAuthStore((s) => s.clear);
  const [tab, setTab] = useState<Tab>('dashboard');

  // Boot: validate persisted token against /me. If the user lost ADMIN role
  // since the token was issued, log them out.
  useEffect(() => {
    if (status !== 'unknown') return;
    if (!token) {
      setStatus('anon');
      return;
    }
    setStatus('loading');
    api
      .me()
      .then(({ user: u }) => {
        if (u.role !== 'ADMIN' && u.role !== 'MODERATOR') {
          clear();
          return;
        }
        setSession(token, u);
      })
      .catch((e) => {
        if (e instanceof ApiError && (e.status === 401 || e.status === 404 || e.status === 403)) {
          clear();
        } else {
          // Network blip — keep cached session
          setStatus('authed');
        }
      });
  }, [status, token, setSession, setStatus, clear]);

  if (status === 'unknown' || status === 'loading') {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          color: 'var(--c-cyan)',
          fontFamily: 'var(--f-display)',
          letterSpacing: '0.16em',
        }}
      >
        LOADING…
      </div>
    );
  }

  if (status !== 'authed' || !user || (user.role !== 'ADMIN' && user.role !== 'MODERATOR')) {
    return <LoginPage />;
  }

  return (
    <div className="shell">
      <aside className="sidebar">
        <div
          className="display"
          style={{ fontSize: 14, color: 'var(--c-cyan)', marginBottom: 16, letterSpacing: '0.1em' }}
        >
          DON’T MISS
          <div
            className="tag"
            style={{ marginTop: 4, color: 'var(--ink-dim)', fontSize: 9 }}
          >
            ADMIN CONSOLE
          </div>
        </div>

        <nav style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {NAV.map((n) => (
            <button
              key={n.id}
              className={`nav-item ${tab === n.id ? 'active' : ''}`}
              onClick={() => setTab(n.id)}
            >
              <span style={{ width: 18 }}>{n.icon}</span>
              {n.label}
            </button>
          ))}
        </nav>

        <div style={{ marginTop: 24, padding: '12px', borderTop: '1px solid var(--hairline)' }}>
          <div className="tag" style={{ marginBottom: 6 }}>
            SIGNED IN
          </div>
          <div style={{ fontSize: 12, fontWeight: 700 }}>{user.displayName}</div>
          <div
            style={{
              fontSize: 10,
              color: 'var(--ink-dim)',
              marginTop: 2,
              fontFamily: 'var(--f-mono)',
            }}
          >
            {user.email}
          </div>
          <button
            onClick={() => clear()}
            className="btn btn-danger"
            style={{ width: '100%', marginTop: 10, fontSize: 11 }}
          >
            SIGN OUT
          </button>
        </div>
      </aside>

      <main className="content">
        {tab === 'dashboard' && <DashboardPage />}
        {tab === 'users' && <UsersPage />}
        {tab === 'settings' && <SettingsPage />}
        {tab === 'bosses' && <BossesPage />}
        {tab === 'frames' && <FramesPage />}
        {tab === 'finishers' && <FinishersPage />}
        {tab === 'backgrounds' && <BackgroundsPage />}
        {tab === 'characters' && <CharactersPage />}
        {tab === 'combos' && <CombosPage />}
        {tab === 'skills' && <SkillsPage />}
        {tab === 'holdskins' && <HoldSkinsPage />}
        {tab === 'clanlogos' && <ClanEmblemsPage />}
        {tab === 'tips' && <TipsPage />}
        {tab === 'audio' && <AudioPage />}
        {tab === 'iap' && <IapPage />}
        {tab === 'push' && <PushPage />}
        {tab === 'audit' && <AuditPage />}
      </main>
    </div>
  );
}
